/**
 * Service Registry
 * Central registry for all API services and mappers
 * Similar to componentRegistry but for backend services
 */

import {
  loanOfficerService,
  authService,
  sessionService,
  branchService,
} from './api';
import { loanOfficerMapper, branchMapper } from './mappers';
import { getTraceId } from '@/utils';

/**
 * Service configuration type
 * Defines the structure of a service call with optional mapper
 */
export interface ServiceConfig {
  service: string;
  args?: Record<string, unknown>;
  mapper?: string;
  mapperArgs?: Record<string, unknown>;
}

/**
 * Service function type
 * All services should return a Promise
 */
export type ServiceFunction<
  TArgs = Record<string, unknown>,
  TResult = unknown,
> = (args: TArgs) => Promise<TResult>;

/**
 * Mapper function type
 * Mappers transform data from one format to another
 */
export type MapperFunction<TInput = unknown, TOutput = unknown> = (
  data: TInput,
  ...args: unknown[]
) => TOutput;

/**
 * Service registry mapping service names to service functions
 */
export const serviceRegistry: Record<string, ServiceFunction> = {
  getLoanOfficers:
    loanOfficerService.getLoanOfficers as unknown as ServiceFunction,
  getLoanOfficerById:
    loanOfficerService.getLoanOfficerById as unknown as ServiceFunction,
  getLoanOfficerByIdentifier:
    loanOfficerService.getLoanOfficerByIdentifier as unknown as ServiceFunction,
  // Auth services
  registerUser: authService.registerUser as unknown as ServiceFunction,
  // Session services
  createSession: sessionService.createSession as unknown as ServiceFunction,
  // Branch services
  getBranches: branchService.getBranches as unknown as ServiceFunction,
  getBranchById: branchService.getBranchById as unknown as ServiceFunction,
};

/**
 * Mapper registry mapping mapper names to mapper functions
 */
export const mapperRegistry: Record<string, MapperFunction> = {
  // Loan Officer mappers
  mapLoanOfficerToComponentFormat:
    loanOfficerMapper.mapLoanOfficerToComponentFormat as unknown as MapperFunction,
  mapLoanOfficersToComponentFormat:
    loanOfficerMapper.mapLoanOfficersToComponentFormat as unknown as MapperFunction,
  // Branch mappers
  mapBranchToComponentFormat:
    branchMapper.mapBranchToComponentFormat as unknown as MapperFunction,
  mapBranchesToComponentFormat:
    branchMapper.mapBranchesToComponentFormat as unknown as MapperFunction,
};

/**
 * Execute a service call with optional mapping
 * Errors from the interceptor are logged and re-thrown to ApiComponent
 * @param config - Service configuration
 * @returns Promise with the service result (optionally mapped)
 * @throws {Error} If service not found, or if HTTP request fails (normalized by interceptor)
 */
export const executeService = async <TResult = unknown>(
  config: ServiceConfig
): Promise<TResult | null> => {
  const { service, args = {}, mapper, mapperArgs = {} } = config;

  // Get the service function
  const serviceFunction = serviceRegistry[service];
  if (!serviceFunction) {
    const error = new Error(`Service "${service}" not found in registry`);
    console.error(
      JSON.stringify({
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: 'Service not found in registry',
        error: error.name,
        stack_trace: error.stack?.replace(/\n/g, '|') || null,
        trace_id: getTraceId(),
      })
    );
    // Throw error instead of returning null for consistency
    throw error;
  }

  try {
    // Execute the service
    const result = await serviceFunction(args);

    // If a mapper is specified, apply it
    if (mapper) {
      const mapperFunction = mapperRegistry[mapper];
      if (!mapperFunction) {
        const error = new Error(`Mapper "${mapper}" not found in registry`);
        console.error(
          JSON.stringify({
            t: new Date().toISOString(),
            severity: 'ERROR',
            message: 'Mapper not found in registry, returning unmapped data',
            error: error.name,
            stack_trace: error.stack?.replace(/\n/g, '|') || null,
            trace_id: getTraceId(),
          })
        );
        return result as TResult | null;
      }

      // Apply mapper with additional args if provided
      const mapperArgsArray = Object.values(mapperArgs);
      return mapperFunction(result, ...mapperArgsArray) as TResult | null;
    }

    return result as TResult | null;
  } catch (error) {
    // Normalize error object
    const normalizedError =
      error instanceof Error ? error : new Error('Unknown service error');

    // Log the error with trace ID for distributed tracing
    console.error(
      JSON.stringify({
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: `Error executing service "${service}"`,
        service_name: service,
        error: normalizedError.name,
        error_message: normalizedError.message,
        stack_trace: normalizedError.stack?.replace(/\n/g, '|') || null,
        trace_id: getTraceId(),
        args: JSON.stringify(args),
      })
    );

    // Re-throw error to propagate to ApiComponent
    // Error is already normalized by interceptor with statuscode and message
    // ApiComponent will catch it and display via Snackbar
    throw normalizedError;
  }
};

/**
 * Execute multiple services in parallel
 * @param configs - Array of service configurations
 * @returns Promise with array of results
 */
export const executeServices = async <TResult = unknown>(
  configs: ServiceConfig[]
): Promise<(TResult | null)[]> => {
  return Promise.all(configs.map((config) => executeService<TResult>(config)));
};
